import { useQuery } from '@tanstack/react-query'
import type React from 'react'
import { createContext, type PropsWithChildren, useContext, useSyncExternalStore } from 'react'
import { hodorService } from 'v2/shared/services/hodor-service'
import {
  EMBED_SESSION_READY_EVENT,
  getEmbedSession,
  isEmbedMode,
} from 'v2/shared/utils/embed-session'
import { featureFlagsQueryKey } from 'v2/shared/utils/query-key-management'
import type { RFeatureFlags, RUserSession } from 'v2/types/api/auth'
import { useAuth } from './use-auth'

export const initialFeatureFlags: RFeatureFlags = {
  useAciIntegrations: false,
  // Hard false, not !isProd: enabling a third-party messenger for an org must be a
  // deliberate PostHog entry, never a side effect of how a build resolved its env.
  onboardingAssistantEnabled: false,
  // Only ever grants Custom SIP Platform; PostHog targets it per account
  customSipPlatformEnabled: false,
  multiChatApps: false,
  isInitialData: true,
  chatDemoQuestions: false,
  integrationApps: false,
  slackTriggerEnabled: false,
  vibeAgentDisabled: false,
  isDebuggingMode: false,
  mcpToolsInAiActionNodeEnabled: false,
  playgroundEnabled: false,
  channelVoiceEnabled: false,
  ssoSelfServeEnabled: false,
  isManualEvalEnabled: false,
  isVibeEvalEnabled: false,
  isSimulationEvalEnabled: false,
  isBetaFeature: false,
  isCashFreeAutoRecharge: false,
  isCashFreeBankTransferEnabled: false,
  isCashFreeTesting: false,
  sipTrunkingGeoPermissionsEnabled: false,
  playgroundStreamTransportEnabled: false,
  isLivekitEnabled: false,
  aiAgentV2_5Enabled: false,
  streamNodeEnabled: false,
  usePlivoStunServer: false,
  isVerifyWhatsAppEnabled: false,
  usageReportsEnabled: false,
  voiceAgentsSplitEnabled: false,
  vrInsightsEnabled: false,
  is140160NumberSeriesEnabled: false,
  uccCallbacksEnabled: false,
  isCurrencyConversionDisabled: false,
  isBuddyDirectTicketEnabled: false,
  buddyFlowChatEnabled: false,
  isBuddyFeedbackDisabled: false,
  supportPortalEnabled: false,
  isEmbeddedCardCheckoutEnabled: false,
  isAINodeDisabled: false,
  tdlcApiTransportEnabled: false,
  kbDocsModeEnabled: false,
  aoConversationQualityEnabled: false,
  isVoiceSandboxEnabled: false,
  subaccountTaggingEnabled: false,
  isIndiaCafEnabled: false,
  scopeTokensEnabled: false,
  trustAndLegalEnabled: false,
}

/**
 * Context for managing feature flags.
 * Exported so tests can nest a provider to override flags — `mock.module` on this
 * file would leak a stubbed `useFeatureFlags` into every later test file.
 */
export const FeatureFlagsContext = createContext<RFeatureFlags>(initialFeatureFlags)

// Embed: this provider mounts above the embed bootstrap, so on a cold boot the
// session does not exist yet; EMBED_SESSION_READY_EVENT is the re-render signal.
const subscribeEmbedSession = (onChange: () => void) => {
  window.addEventListener(EMBED_SESSION_READY_EVENT, onChange)
  return () => window.removeEventListener(EMBED_SESSION_READY_EVENT, onChange)
}
const embedSessionIdSnapshot = () => (isEmbedMode() ? getEmbedSession()?.session_id : undefined)

// Feature flag
const useFeatureFlagsQuery = (userAuthenticated: boolean, auth?: RUserSession) => {
  const embedSessionId = useSyncExternalStore(subscribeEmbedSession, embedSessionIdSnapshot)

  return useQuery({
    // Embed has no console user: key per embed session. hodor evaluates the embed
    // request from the session's org and ignores the email/org body.
    queryKey: isEmbedMode()
      ? featureFlagsQueryKey('embed', embedSessionId)
      : featureFlagsQueryKey(auth?.email, auth?.organization_name),
    staleTime: 0,
    enabled: isEmbedMode() ? !!embedSessionId : userAuthenticated,
    refetchOnReconnect: false,
    meta: {
      silent: true,
    },
    retry: false,
    queryFn: async () => {
      const response = await hodorService.getFeatureFlags({
        user_email: auth?.email || '',
        org_name: auth?.organization_name || '',
      })
      // Spread defaults under the fetched payload so flags absent from hodor's
      // response keep their intended fallback declared in `initialFeatureFlags`
      // instead of coming back as `undefined` after the first successful fetch.
      // isInitialData is a client-side "flags not fetched yet" marker, not a
      // hodor flag — the defaults spread must not carry it past a successful
      // fetch, or consumers gating on it treat flags as loading forever and
      // publishes omit livekit_ready, which the backend deletes (SER-5426).
      return { ...initialFeatureFlags, ...response.data, isInitialData: false }
    },
    initialData: initialFeatureFlags,
  })
}

/**
 * Provider component for the FeatureFlagsContext.
 * @param children - The child components.
 */
export const FeatureFlagsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { userAuthenticated, details } = useAuth()
  const { data } = useFeatureFlagsQuery(userAuthenticated, details)

  return <FeatureFlagsContext.Provider value={data}>{children}</FeatureFlagsContext.Provider>
}

/**
 * Hook for accessing the feature flags from the FeatureFlagsContext.
 * @returns The feature flags.
 */
export const useFeatureFlags = () => useContext(FeatureFlagsContext)
